import React from "react";
import { useTranslation } from "react-i18next";
import { LoadingSpinner } from "@/components/common/loading-spinner";
import { MapPin, Edit2, Trash2, Plus } from "lucide-react";
import { Address } from "@/api/models/api-types";
import { useAddresses } from "../addresses/hooks/use-addresses";
import { DeleteAddressDialog, AddAddressDialog } from "../addresses/components";

export const MyAddressesTab: React.FC = () => {
  const { t } = useTranslation();

  // Feature hook - handles all state and actions
  const {
    addresses,
    isLoading,
    isError,
    error,
    openDeleteAddressDialog,
    openEditAddressDialog,
  } = useAddresses();

  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="flex flex-col">
        {/* Header with Title */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2.5 md:gap-4 px-4 md:px-6 py-3 md:py-5 bg-bg-tertiary dark:bg-gray-800 border-b border-border-secondary dark:border-gray-600">
          <h2 className="text-base md:text-xl font-semibold text-text-primary dark:text-text-primary">
            {t("common.myAddresses")}
          </h2>
        </div>

        <div className="p-8 md:p-12 text-center bg-white dark:bg-gray-800">
          <h2 className="text-base md:text-xl font-semibold text-text-primary dark:text-text-primary mb-1.5 md:mb-2">
            {t("addresses.list.errorTitle")}
          </h2>
          <p className="text-xs md:text-sm text-text-tertiary dark:text-text-tertiary">
            {error?.message || t("addresses.list.errorMessage")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Header with Title and Add Button */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5 md:gap-4 px-4 md:px-6 py-3 md:py-5 bg-bg-tertiary dark:bg-gray-800 border-b border-border-secondary dark:border-gray-600">
        <h2 className="text-base md:text-xl font-semibold text-text-primary dark:text-text-primary">
          {t("common.myAddresses")} ({addresses.length})
        </h2>
        <button
          onClick={() => openEditAddressDialog("")}
          className="flex items-center justify-center gap-1.5 md:gap-2 px-2.5 md:px-3 py-1.5 md:py-2 text-xs md:text-sm font-semibold text-indigo-500 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors border border-indigo-400 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
        >
          <Plus className="w-3.5 h-3.5 md:w-4 md:h-4" />
          <span className="whitespace-nowrap">{t("addresses.addAddress")}</span>
        </button>
      </div>

      {/* Addresses Grid */}
      <div className="px-4 md:px-6 py-4 md:py-6">
        {addresses.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 md:gap-4">
            {addresses.map((address) => (
              <AddressCard
                key={address._id}
                address={address}
                onEdit={() => openEditAddressDialog(address._id)}
                onDelete={() => openDeleteAddressDialog(address._id)}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center px-4 py-10 md:py-20">
            {/* Empty State Icon */}
            <div className="bg-bg-tertiary dark:bg-gray-700 rounded-full p-2.5 md:p-3 mb-2.5 md:mb-3">
              <MapPin className="w-5 h-5 text-text-tertiary dark:text-gray-400" />
            </div>

            {/* Empty State Text */}
            <p className="text-xs md:text-base text-text-secondary dark:text-gray-300 text-center">
              {t("addresses.list.emptyMessage")}
            </p>
          </div>
        )}
      </div>

      {/* Dialogs - Zero Props */}
      <DeleteAddressDialog />
      <AddAddressDialog />
    </div>
  );
};

// Address Card Component
interface AddressCardProps {
  address: Address;
  onEdit: () => void;
  onDelete: () => void;
}

const AddressCard: React.FC<AddressCardProps> = ({
  address,
  onEdit,
  onDelete,
}) => {
  return (
    <div className="border border-border-secondary dark:border-gray-600 rounded-xl p-2.5 md:p-4 hover:border-indigo-400 hover:shadow-sm dark:hover:border-indigo-500 transition-all">
      {/* Header: Label and Actions */}
      <div className="flex items-start justify-between mb-1.5 md:mb-3">
        <p className="text-[10px] md:text-sm font-medium text-text-tertiary dark:text-gray-400 uppercase tracking-wide">
          {address.label}
        </p>
        <div className="flex items-center gap-1 md:gap-2">
          {/* Edit Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="p-1 md:p-2 border border-border-secondary dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            aria-label="Edit address"
          >
            <Edit2 className="w-3 h-3 md:w-4 md:h-4 text-text-primary dark:text-gray-300" />
          </button>
          {/* Delete Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="p-1 md:p-2 border border-border-secondary dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            aria-label="Delete address"
          >
            <Trash2 className="w-3 h-3 md:w-4 md:h-4 text-red-500 dark:text-red-400" />
          </button>
        </div>
      </div>

      {/* Name */}
      <p className="text-xs md:text-base font-semibold text-text-primary dark:text-gray-100 mb-1 md:mb-2">
        {address.firstName} {address.lastName}
      </p>

      {/* Address Lines */}
      <div className="text-[11px] md:text-sm text-text-secondary dark:text-gray-300 space-y-0.5">
        <p>{address.addressLine1}</p>
        {address.addressLine2 && <p>{address.addressLine2}</p>}
        <p>
          {address.city}, {address.state} {address.postalCode}
        </p>
        {address.country && (
          <p className="text-text-tertiary dark:text-gray-400 mt-0.5 md:mt-1">
            {address.country}
          </p>
        )}
      </div>
    </div>
  );
};
